// =====================================================================
// imageOverrideLogic — pure precedence/merge rules behind every
// admin-editable image on the public landing page: team photos, partner
// logos, and standalone section images (e.g. the IEWRS Framework
// diagram). Shared by ImageManager.jsx (admin preview while editing) and
// LandingPage.jsx (public read side) so both sides agree on exactly the
// same rules — extracted here instead of living inline in either
// component so the rules are covered by node --test, not just implied by
// whatever each component happens to do.
//
// Companion to the server-side cache-busting fix in
// services/auth/utils/imageDataUrl.js (cacheBustVersion): that half stops
// a STABLE per-id photo/logo URL from hiding a re-upload behind the
// browser's 24h image cache. This half stops the two OTHER ways a saved
// edit can fail to reflect: a stale client-side cache (localStorage)
// silently winning over what the server actually has, and a failed/empty
// fetch blanking a section instead of degrading to the hardcoded default.
// =====================================================================

/**
 * Normalize whatever GET /api/public/images (or a localStorage snapshot
 * of it) returned into the exact three maps every caller expects, so a
 * missing key or a malformed non-object value never has to be
 * special-cased at every call site. Never returns undefined for any of
 * the three keys.
 *
 * @param {unknown} raw
 * @returns {{ teamImages: Record<string, unknown>, partnerLogos: Record<string, unknown>, sectionImages: Record<string, unknown> }}
 */
export function normalizeImageOverrides(raw) {
  const d = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const asMap = (v) => (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
  return {
    teamImages: asMap(d.teamImages),
    partnerLogos: asMap(d.partnerLogos),
    sectionImages: asMap(d.sectionImages),
  };
}

/**
 * The one precedence rule every image on the landing page follows: an
 * explicit override wins, a falsy override (missing, null, empty string
 * — the shape a "reset to default" save produces) falls back to the
 * hardcoded default, and if there's no default either the caller gets
 * null (renders as "no image" rather than a broken <img src="">).
 *
 * @param {string | null | undefined} override
 * @param {string | null | undefined} fallback
 * @returns {string | null}
 */
export function resolveImageOverride(override, fallback) {
  return override || fallback || null;
}

/**
 * Admin-side (ImageManager) preview precedence — one image slot has THREE
 * candidate sources while the operator is mid-edit: an explicit "reset to
 * default" pending for this id, an unsaved local draft upload, and
 * whatever the server last persisted. Reset always wins (it's the most
 * recent explicit action); otherwise the draft (if any) wins over the
 * persisted server value, which is exactly resolveImageOverride's rule
 * one layer up.
 *
 * @param {{ isReset: boolean, draftValue?: string | null, serverValue?: string | null, fallback?: string | null }} args
 * @returns {string | null}
 */
export function resolveDraftImage({ isReset, draftValue, serverValue, fallback }) {
  if (isReset) return resolveImageOverride(null, fallback);
  return resolveImageOverride(draftValue || serverValue, fallback);
}

/**
 * LandingPage's loadImageOverrides() precedence: try the API first (the
 * durable, shared source every admin edit writes to); only reach for a
 * client-local snapshot (localStorage, pre-API-migration leftover, or a
 * transient offline cache) when the API is unavailable; and if BOTH are
 * unavailable, degrade to an explicitly-empty overrides object rather
 * than throwing or leaving state undefined — every image on the page then
 * falls back to its hardcoded default via resolveImageOverride() instead
 * of the section going blank.
 *
 * Deliberately does NOT merge API + localStorage: a successful API
 * response is authoritative and completely replaces any local snapshot,
 * so a stale localStorage entry from a previous device/session can never
 * override what an admin just saved through the editor. This is the
 * "source mismatch" failure mode named in the reflection-gap bug this
 * module was extracted to fix — before this, a partial-failure code path
 * could plausibly have left a merge of both in place.
 *
 * @param {{ apiOk: boolean, apiData?: unknown, localStorageRaw?: string | null }} args
 * @returns {{ teamImages: Record<string, unknown>, partnerLogos: Record<string, unknown>, sectionImages: Record<string, unknown> }}
 */
export function resolveImageOverridesSource({ apiOk, apiData, localStorageRaw }) {
  if (apiOk) return normalizeImageOverrides(apiData);

  if (typeof localStorageRaw === 'string' && localStorageRaw.length > 0) {
    try {
      return normalizeImageOverrides(JSON.parse(localStorageRaw));
    } catch {
      // Malformed local snapshot — degrade to empty rather than throw.
      return normalizeImageOverrides(null);
    }
  }

  return normalizeImageOverrides(null);
}

/**
 * Resolve a single standalone section image (e.g. `framework`) against
 * its normalized override map and the page's hardcoded default path.
 * Thin wrapper over resolveImageOverride kept separate so call sites read
 * as intent ("the framework image") rather than a raw map lookup.
 *
 * @param {Record<string, unknown>} sectionImages
 * @param {string} slug
 * @param {string | null} fallback
 * @returns {string | null}
 */
export function resolveSectionImage(sectionImages, slug, fallback) {
  const map = sectionImages && typeof sectionImages === 'object' ? sectionImages : {};
  return resolveImageOverride(map[slug], fallback);
}
